import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { TypedUseQueryHookResult } from '@reduxjs/toolkit/query/react';

import { useUserInfo } from 'hooks/useUserInfo';
import type { IPaymentMethod } from 'redux-store/api/billing/billing.types';
import {
  useGetCurrentPaymentPlanQuery,
  useGetPaymentMethodsQuery,
} from 'redux-store/api/billing/billing.api';
import { useAppSelector } from 'redux-store/hooks/useAppSelector';
import { authSelector } from 'redux-store/selector';

type TUsePaymentMethodQueryOptions = Parameters<typeof useGetPaymentMethodsQuery>[1];

type TBillingBaseQuery = BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>;

export type TUsePaymentMethodQueryResult = TypedUseQueryHookResult<
  IPaymentMethod | null,
  void,
  TBillingBaseQuery
>;

export const usePaymentMethodQuery = (
  options?: TUsePaymentMethodQueryOptions
): TUsePaymentMethodQueryResult => {
  const { companyId } = useAppSelector(authSelector);
  const { canManageBillingPlans } = useUserInfo();

  const { data: currentPaymentPlan, isLoading: isCurrentPlanLoading } =
    useGetCurrentPaymentPlanQuery(undefined, {
      skip: !companyId,
    });

  const shouldSkipPaymentMethods =
    !companyId ||
    !canManageBillingPlans ||
    isCurrentPlanLoading ||
    currentPaymentPlan?.hasPaymentMethod === false;

  const result = useGetPaymentMethodsQuery(undefined, {
    ...options,
    skip: options?.skip || shouldSkipPaymentMethods,
  });

  return {
    ...result,
    isLoading: isCurrentPlanLoading || result.isLoading,
  } as TUsePaymentMethodQueryResult;
};
